<template>
  <nk-page :commercialAccess="['Salary']" class="personnel-category" :policyClaims="{ or: ['Salary_PersonnelCategory_Management'] }">
    <template #badges>
      <nk-badge system="salary">
        <nk-badge-item type="system" modelValue="salary"></nk-badge-item>
        <nk-badge-item type="system" modelValue="salary" :title="$t('forms.Personnel')"></nk-badge-item>
        <nk-badge-item type="system" modelValue="salary" :title="$t('forms.PersonnelCategory')"></nk-badge-item>
      </nk-badge>
    </template>
    <div class="nk-card h-100 d-flex flex-column" startup>
      <div class="h-grow-lock">
        <nk-table v-model="selectedRow" :data="categories" :options="tableOptions" ref="categoriesList" tabindex="101" @row_dbClicked="rowDbClicked">
        </nk-table>
      </div>
    </div>
  </nk-page>
</template>
<script lang="ts">
  import Merge from 'lodash/merge'
  import Input from '~/components/general/nikan-input.vue'
  import $sc from '~/plugins/shortcut.collection.js'
  import dialog from '~/plugins/nikan-dialog-plugin'
  import validators from '~/statics/validators'
  import { defineComponent } from 'vue'
  import { TYPE } from 'vue-toastification'
  import { PersonnelCategory } from '@/classes/salary/personnel-category'
  import { post } from '~/plugins/nikan-api'
  import Endpoints from '@nikan/endpoints'
  import { IResponseList } from '@/interfaces/base'


  export default defineComponent({
    modal: {
      size: 'custom',
      width: 600,
      height: 700,
      stateUID: 'personnel-category-selector',
      helpTag: 'PersonnelCategoryList'
    },
    props: {
      options: { type: Object },
    },
    data() {
      return {
        settings: {
          type: null,
          title: null
        },
        categories: [] as PersonnelCategory[],
        selectedRow: null,
        tableOptions: {
          activeRowDisabled: true,
          editable: this.$nikan.checkPermission({ or: ['Salary_PersonnelCategory_Add', 'Salary_PersonnelCategory_Edit'] }),
          searchable: false,
          allowNewRow: true,
          alwaysEmit: true,
          buttons: {
            addBtn: {
              options: {
                visible: true,
                policyClaims: 'Salary_PersonnelCategory_Add',
              }
            },
            deleteBtn: {
              options: {
                visible: true,
                policyClaims: 'Salary_PersonnelCategory_Delete',
              }
            }
          },
          displayOrderEnabled: false,
          prepareNewRow: () => {
            return this.generateEmptyRow()
          },
          columns: [
            {
              name: 'rowNumber',
              title: this.$t('row-number'),
              searchable: true,
              sortable: true,
              editable: false,
              width: 20,
              entering: false,
              pinned: true,
              pinnable: false,
              resizable: false,
            },
            {
              name: 'name',
              title: this.$t('category.f.name'),
              edit: {
                component: Input,
                options: {
                  mode: 'string',
                },
              },
              searchable: true,
              sortable: false,
              editable: true,
              width: 150,
              validators: validators('required'),
            },
          ],
        },
        pageButtons: {
          saveButton: {
            title: this.$t('save'),
            shortcut: $sc.find('save'),
            icon: 'save',
            visible: true,
            disabled: false,
            isPrimary: true,
            handle: () => {
              this.save().then(() => {
                this.resolve()
              })
            },
            policyClaims: {
              or: ['Salary_PersonnelCategory_Add', 'Salary_PersonnelCategory_Edit', 'Salary_PersonnelCategory_Delete']
            },
          },
        }
      }
    },
    watch: {
      options: {
        immediate: true,
        deep: true,
        handler(val) {
          this.settings = Merge(this.settings, val)
        },
      },
    },
    mounted() {
      this.loadCategories()

      let buttonsArray = [
        this.pageButtons.saveButton,
      ]

      this.prepareWindow(this.$t('category.list', {
        type: this.$t('salary.personnel.title'),
      }), buttonsArray)
    },
    methods: {
      isTableChanged(tableData: (PersonnelCategory & { rowState: string })[]): void | true {
        if (tableData.findIndex((data) => data.rowState !== 'unchanged') > -1) return true
        else
          this.$toast(this.$t('category.m.save.table-unchanged'), { type: TYPE.INFO })
      },
      generateEmptyRow: () => new PersonnelCategory(),
      saveCategories(categories: (PersonnelCategory & { rowState: string })[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
          post(Endpoints.getUrl('personnelCategory.modify'), { personnelCategories: categories }).then(() => {
            resolve()
          }).catch(() => {
            reject()
          })
        })
      },
      save(): Promise<PersonnelCategory[]> {
        return new Promise((resolve, reject) => {
          this.$refs.categoriesList
            .commitChanges()
            .then((categories: (PersonnelCategory & { rowState: string })[]) => {
              if (this.isTableChanged(categories)) {
                this.saveCategories(categories).then(() => {
                  this.$toast(this.$t('salary.personnel.m.category-save-success'), { type: TYPE.SUCCESS })
                  resolve(categories)
                })
              }
            })
            .catch((errorRows: { message: string } | { rowNumber: number }[]) => {
              if (Array.isArray(errorRows)) {
                var rowNumbers = errorRows.map((x, i) => x.rowNumber).join(',')
                dialog({
                  title: this.$t('category.m.save-category-title'),
                  body: this.$t('category.m.save.error.rows', { rowNumbers }),
                  type: 'error',
                  isPrimary: true,
                })
              } else {
                dialog({
                  title: this.$t('category.m.save-category-title'),
                  body: errorRows.message,
                  type: 'error',
                  isPrimary: true,
                })
              }

              reject(new Error('invalid-rows'))
              this.defer(() => this.$refs.categoriesList.$el.focus(), 100)
            })
        })
      },
      rowDbClicked(val: PersonnelCategory): void {
        if (this.grandParentType() === 'modal')
          this.resolve(val)
      },
      loadCategories(): void {
        let wait = this.$wait(this.$el)
        post(Endpoints.getUrl('personnelCategory.list'), {}).then((res: IResponseList<PersonnelCategory>) => {
          wait.end()
          this.categories = res.data.items
        }).catch(() => {
          wait.end()
        })
      }
    },
  })
</script>
<style></style>
@/classes/salary/personnel-category